/**
 * Component: QueryLogPage
 * SpecReferenceID: ADMIN-V1
 * Requirements: ADMIN-V1-R02
 *
 * Admin page for displaying user query logs with filtering, pagination,
 * and export capabilities.
 * ADMIN-V1-R02: System shall display user query logs with filtering,
 *               pagination, and export capabilities.
 * Accessibility: WCAG 2.1 AA compliant
 */

import React, { useState, useCallback, useEffect, useMemo } from 'react';
import {
  MessageSquareText,
  Loader2,
  AlertCircle,
  ArrowUp,
  ArrowDown,
  ArrowUpDown,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import type { DateRange } from 'react-day-picker';
import { subDays, format } from 'date-fns';

import api, { getErrorMessage } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { ExportButton, type ExportData } from './components/ExportButton';
import { DateRangePicker } from './components/DateRangePicker';

// -- Types --------------------------------------------------------------------

interface QueryLogEntry {
  id: string;
  timestamp: string;
  user_id: string;
  session_id: string;
  correlation_id: string;
  query_text: string;
  query_type: string;
  response_time_ms: number | null;
  result_count: number | null;
  safety_decision: string | null;
  extra: Record<string, unknown>;
}

interface QueryLogsResponse {
  logs: QueryLogEntry[];
  total_count: number;
  offset: number;
  limit: number;
  has_more: boolean;
}

type SortField = 'timestamp' | 'user_id' | 'session_id' | 'query_text' | 'safety_decision' | 'query_type';
type SortDirection = 'asc' | 'desc';

const PAGE_SIZES = [10, 25, 50] as const;
const QUERY_TYPES = ['all', 'search', 'chat'] as const;

// -- Helpers ------------------------------------------------------------------

function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function statusBadgeVariant(status: string | null): 'default' | 'secondary' | 'destructive' | 'outline' {
  switch (status) {
    case 'allow':
      return 'default';
    case 'block':
      return 'destructive';
    case 'clarify':
      return 'secondary';
    default:
      return 'outline';
  }
}

// -- Component ----------------------------------------------------------------

export function QueryLogPage(): React.ReactElement {
  // Data state
  const [logs, setLogs] = useState<QueryLogEntry[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filter state
  const [dateRange, setDateRange] = useState<DateRange | undefined>({
    from: subDays(new Date(), 7),
    to: new Date(),
  });
  const [queryTypeFilter, setQueryTypeFilter] = useState<string>('all');
  const [textFilter, setTextFilter] = useState<string>('');
  const [appliedTextFilter, setAppliedTextFilter] = useState<string>('');

  // Pagination state
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<number>(25);

  // Sort state
  const [sortField, setSortField] = useState<SortField>('timestamp');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

  // Expanded rows
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));

  // -- Fetch logs -------------------------------------------------------------

  const fetchLogs = useCallback(async () => {
    if (!dateRange?.from || !dateRange?.to) return;

    setLoading(true);
    setError(null);
    try {
      const params: Record<string, string | number> = {
        start_time: dateRange.from.toISOString(),
        end_time: dateRange.to.toISOString(),
        offset: (page - 1) * pageSize,
        limit: pageSize,
      };
      if (queryTypeFilter !== 'all') {
        params['query_type'] = queryTypeFilter;
      }

      const response = await api.get<QueryLogsResponse>('/admin/logs/queries', { params });
      let fetchedLogs = response.data.logs;

      // Client-side text filter on query content
      if (appliedTextFilter) {
        const lowerFilter = appliedTextFilter.toLowerCase();
        fetchedLogs = fetchedLogs.filter((log) =>
          log.query_text.toLowerCase().includes(lowerFilter),
        );
      }

      setLogs(fetchedLogs);
      setTotalCount(appliedTextFilter ? fetchedLogs.length : response.data.total_count);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [dateRange, page, pageSize, queryTypeFilter, appliedTextFilter]);

  useEffect(() => {
    void fetchLogs();
  }, [fetchLogs]);

  // Reset page when filters change
  useEffect(() => {
    setPage(1);
  }, [dateRange, queryTypeFilter, appliedTextFilter, pageSize]);

  // -- Text filter submit -----------------------------------------------------

  const handleTextFilterSubmit = useCallback((e: React.FormEvent) => {
    e.preventDefault();
    setAppliedTextFilter(textFilter);
  }, [textFilter]);

  const handleClearFilters = useCallback(() => {
    setTextFilter('');
    setAppliedTextFilter('');
    setQueryTypeFilter('all');
    setDateRange({
      from: subDays(new Date(), 7),
      to: new Date(),
    });
  }, []);

  // -- Row expansion ----------------------------------------------------------

  const toggleRow = useCallback((id: string) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  // -- Sorting ----------------------------------------------------------------

  const handleSort = useCallback((field: SortField) => {
    setSortField((prev) => {
      if (prev === field) {
        setSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'));
        return prev;
      }
      setSortDirection('asc');
      return field;
    });
  }, []);

  const sortedLogs = useMemo(() => {
    return [...logs].sort((a, b) => {
      const aVal = String(a[sortField] ?? '').toLowerCase();
      const bVal = String(b[sortField] ?? '').toLowerCase();
      const cmp = aVal.localeCompare(bVal);
      return sortDirection === 'asc' ? cmp : -cmp;
    });
  }, [logs, sortField, sortDirection]);

  // -- Sort header button -----------------------------------------------------

  const SortButton = useCallback(
    ({ field, label }: { field: SortField; label: string }) => {
      const isActiveSort = sortField === field;
      const Icon = isActiveSort
        ? sortDirection === 'asc'
          ? ArrowUp
          : ArrowDown
        : ArrowUpDown;

      return (
        <Button
          variant="ghost"
          size="sm"
          className="-ml-3 h-8"
          onClick={() => handleSort(field)}
          aria-label={`Sort by ${label}${isActiveSort ? `, currently ${sortDirection === 'asc' ? 'ascending' : 'descending'}` : ''}`}
        >
          {label}
          <Icon className="ml-1 h-3.5 w-3.5" aria-hidden="true" />
        </Button>
      );
    },
    [sortField, sortDirection, handleSort],
  );

  // -- Export data -------------------------------------------------------------

  const exportData: ExportData = useMemo(() => {
    const dateStr = dateRange?.from && dateRange?.to
      ? `${format(dateRange.from, 'yyyy-MM-dd')}_to_${format(dateRange.to, 'yyyy-MM-dd')}`
      : 'all-time';

    const rows = logs.map((log) => ({
      Timestamp: log.timestamp,
      'User ID': log.user_id,
      'Session ID': log.session_id,
      'Query Text': log.query_text,
      'Query Type': log.query_type,
      'Response Status': log.safety_decision ?? 'N/A',
      'Response Time (ms)': log.response_time_ms != null ? String(log.response_time_ms) : '',
      'Result Count': log.result_count != null ? String(log.result_count) : '',
    }));

    return {
      filename: `query-logs_${dateStr}`,
      headers: ['Timestamp', 'User ID', 'Session ID', 'Query Text', 'Query Type', 'Response Status', 'Response Time (ms)', 'Result Count'],
      rows,
    };
  }, [dateRange, logs]);

  // -- Pagination handler -----------------------------------------------------

  const handlePageSizeChange = useCallback((value: string) => {
    setPageSize(Number(value));
  }, []);

  // -- Render -----------------------------------------------------------------

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
            <MessageSquareText className="h-6 w-6" aria-hidden="true" />
            Query Logs
          </h1>
          <p className="text-muted-foreground mt-1">
            User query log entries with filtering, pagination, and export.
          </p>
        </div>
        <ExportButton data={exportData} disabled={loading} />
      </div>

      {/* Error alert */}
      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" aria-hidden="true" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Filters</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col gap-4 sm:flex-row sm:items-end">
            <div className="flex-1">
              <DateRangePicker
                dateRange={dateRange}
                onDateRangeChange={setDateRange}
                disabled={loading}
              />
            </div>
            <div className="flex gap-2">
              <Select value={queryTypeFilter} onValueChange={setQueryTypeFilter}>
                <SelectTrigger className="w-[140px]" aria-label="Filter by query type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {QUERY_TYPES.map((type) => (
                    <SelectItem key={type} value={type}>
                      {type === 'all' ? 'All Types' : type.charAt(0).toUpperCase() + type.slice(1)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <form onSubmit={handleTextFilterSubmit} className="flex gap-2">
                <Input
                  type="text"
                  placeholder="Filter by keyword..."
                  value={textFilter}
                  onChange={(e) => setTextFilter(e.target.value)}
                  className="w-[180px]"
                  aria-label="Filter by query text"
                />
                <Button type="submit" variant="secondary" size="sm" disabled={loading}>
                  Apply
                </Button>
              </form>
            </div>
          </div>
          {appliedTextFilter && (
            <div className="mt-3 flex items-center gap-2">
              <Badge variant="secondary">Text: {appliedTextFilter}</Badge>
              <Button variant="ghost" size="sm" onClick={handleClearFilters}>
                Clear all filters
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Logs table */}
      <Card>
        <CardHeader className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <CardTitle className="text-lg">
            Log Entries
            {!loading && (
              <span className="ml-2 text-sm font-normal text-muted-foreground">
                ({totalCount.toLocaleString()} total)
              </span>
            )}
          </CardTitle>
          <Select value={String(pageSize)} onValueChange={handlePageSizeChange}>
            <SelectTrigger className="w-[100px]" aria-label="Rows per page">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PAGE_SIZES.map((size) => (
                <SelectItem key={size} value={String(size)}>
                  {size} rows
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12" aria-label="Loading logs">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" aria-hidden="true" />
              <span className="sr-only">Loading query logs...</span>
            </div>
          ) : sortedLogs.length === 0 ? (
            <p className="text-center text-muted-foreground py-12">
              No log entries found for the selected filters.
            </p>
          ) : (
            <>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[40px]" />
                      <TableHead>
                        <SortButton field="timestamp" label="Timestamp" />
                      </TableHead>
                      <TableHead>
                        <SortButton field="user_id" label="User ID" />
                      </TableHead>
                      <TableHead>
                        <SortButton field="session_id" label="Session ID" />
                      </TableHead>
                      <TableHead>
                        <SortButton field="query_text" label="Query Text" />
                      </TableHead>
                      <TableHead>
                        <SortButton field="safety_decision" label="Response Status" />
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {sortedLogs.map((log) => (
                      <React.Fragment key={log.id}>
                        <TableRow
                          className="cursor-pointer hover:bg-muted/50"
                          onClick={() => toggleRow(log.id)}
                          aria-expanded={expandedRows.has(log.id)}
                        >
                          <TableCell>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-6 w-6 p-0"
                              aria-label={expandedRows.has(log.id) ? 'Collapse row' : 'Expand row'}
                              tabIndex={-1}
                            >
                              {expandedRows.has(log.id) ? (
                                <ChevronUp className="h-4 w-4" aria-hidden="true" />
                              ) : (
                                <ChevronDown className="h-4 w-4" aria-hidden="true" />
                              )}
                            </Button>
                          </TableCell>
                          <TableCell className="text-sm text-muted-foreground whitespace-nowrap">
                            {formatDateTime(log.timestamp)}
                          </TableCell>
                          <TableCell className="font-medium max-w-[120px] truncate" title={log.user_id}>
                            {log.user_id}
                          </TableCell>
                          <TableCell className="text-sm max-w-[120px] truncate" title={log.session_id}>
                            {log.session_id}
                          </TableCell>
                          <TableCell className="max-w-[250px] truncate" title={log.query_text}>
                            {log.query_text}
                          </TableCell>
                          <TableCell>
                            <Badge variant={statusBadgeVariant(log.safety_decision)}>
                              {log.safety_decision ?? 'N/A'}
                            </Badge>
                          </TableCell>
                        </TableRow>
                        {expandedRows.has(log.id) && (
                          <TableRow>
                            <TableCell colSpan={6} className="bg-muted/30 p-4">
                              <div className="space-y-3 text-sm">
                                <div>
                                  <span className="font-medium">Full Query:</span>
                                  <p className="mt-1 whitespace-pre-wrap text-muted-foreground">{log.query_text}</p>
                                </div>
                                <div className="flex flex-wrap gap-6 text-muted-foreground">
                                  <span>Correlation ID: {log.correlation_id}</span>
                                  <span>Query Type: {log.query_type}</span>
                                  {log.response_time_ms != null && (
                                    <span>Response Time: {log.response_time_ms.toFixed(1)}ms</span>
                                  )}
                                  {log.result_count != null && (
                                    <span>Results: {log.result_count}</span>
                                  )}
                                </div>
                              </div>
                            </TableCell>
                          </TableRow>
                        )}
                      </React.Fragment>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* Pagination */}
              <div className="flex items-center justify-between pt-4">
                <p className="text-sm text-muted-foreground">
                  Showing {(page - 1) * pageSize + 1}–{Math.min(page * pageSize, totalCount)} of {totalCount.toLocaleString()} entries
                </p>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page <= 1}
                    onClick={() => setPage((p) => p - 1)}
                    aria-label="Previous page"
                  >
                    <ChevronLeft className="h-4 w-4" aria-hidden="true" />
                  </Button>
                  <span className="text-sm text-muted-foreground">
                    Page {page} of {totalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page >= totalPages}
                    onClick={() => setPage((p) => p + 1)}
                    aria-label="Next page"
                  >
                    <ChevronRight className="h-4 w-4" aria-hidden="true" />
                  </Button>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default QueryLogPage;
